'use client';

import { GBP_MULTIPLIER, USD_MULTIPLIER } from '@/utils/currency';
import React, { ReactNode, createContext, useContext, useMemo } from 'react';

export type Currency = {
  label: string;
  value: string;
  symbol: '€' | '£' | '$';
};

type CurrencyContextProps = {
  currency: Currency;
  applyCurrency: (value: number) => number;
};

type CurrencyProviderProps = {
  cookieCurrency?: 'EUR' | 'USD' | 'GBP';
  children: ReactNode;
};

const CurrencyContext = createContext<CurrencyContextProps | undefined>(
  undefined,
);

const currencyMap: Currency[] = [
  {
    label: 'Euro',
    symbol: '€',
    value: 'EUR',
  },
  {
    label: 'Dollar',
    symbol: '$',
    value: 'USD',
  },
  {
    label: 'Pound',
    symbol: '£',
    value: 'GBP',
  },
];

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyContext');
  }
  return context;
};

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({
  cookieCurrency = 'EUR',
  children,
}) => {
  const currency = useMemo(
    () => currencyMap.find((c) => c.value === cookieCurrency) ?? currencyMap[0],
    [cookieCurrency],
  );

  const applyCurrency = (value: number) => {
    if (currency.value === 'USD') return Math.round(value * USD_MULTIPLIER);
    if (currency.value === 'GBP') return Math.round(value * GBP_MULTIPLIER);
    return Math.round(value);
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        applyCurrency,
      }}>
      {children}
    </CurrencyContext.Provider>
  );
};
